import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { useFaireDirectBrand } from "@faire/retailer-visitor-shared/hooks/FaireDirect/useFaireDirectBrand/useFaireDirectBrand";
import * as RetailerPaths from "@faire/retailer-visitor-shared/paths/index";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { faireBaseUrl } from "@faire/web--source/common/routes/faireBaseUrl";
import { trackEvent } from "@faire/web--source/common/trackEvent";
import { Button } from "@faire/web--source/slate/Button";
import { Row } from "@faire/web--source/slate/Layout";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { Anchor } from "@faire/web--source/ui/LinkAnchor";
import { trackRetailerPortalBreadcrumbHomeView } from "@faire/web-api--source/events/retailerPortal/view/breadcrumbHome";
import * as React from "react";
import { useEffect, useState } from "react";
import styled from "styled-components";
/**
 * This should be moved to @retailer. It depends on it, and is only used there
 * @trackcomponent
 * @supersededBy "@faire/web/ui/Breadcrumbs/Breadcrumbs#Breadcrumbs"
 */ export var Breadcrumb = function(props) {
    var strictLocalize = useStrictLocalization().strictLocalize;
    var className = props.className, verticalPadding = props.verticalPadding, horizontalPadding = props.horizontalPadding, hideRoot = props.hideRoot, dataTestId = props.dataTestId, onClick = props.onClick;
    var _useState = _sliced_to_array(useState(props.options), 2), options = _useState[0], setOptions = _useState[1];
    var isFaireDirectActiveInLoggedOut = useFaireDirectBrand().isFaireDirectActiveInLoggedOut;
    useEffect(function() {
        setOptions(props.options);
    }, [
        props.options
    ]);
    var handleClick = function(index, option) {
        onClick === null || onClick === void 0 ? void 0 : onClick(option);
        setOptions(options.slice(0, index + 1));
        var optionAtIndex = options[index];
        if (optionAtIndex) {
            // eslint-disable-next-line react-compiler/react-compiler
            optionAtIndex.path = undefined;
        }
        if (option.eventId) {
            trackEvent("breadcrumb/link/click/".concat(option.eventId), true);
        }
    };
    var homeMessage = strictLocalize({
        id: "X36fvs",
        defaultMessage: [
            {
                type: 0,
                value: "Home"
            }
        ]
    });
    return /*#__PURE__*/ _jsx(BreadcrumbRow, {
        className: className,
        $verticalPadding: verticalPadding,
        $horizontalPadding: horizontalPadding,
        "data-test-id": dataTestId,
        role: "navigation",
        "aria-label": strictLocalize({
            id: "2ezgqJ",
            defaultMessage: [
                {
                    type: 0,
                    value: "Breadcrumbs"
                }
            ]
        }),
        children: /*#__PURE__*/ _jsxs(StyledList, {
            children: [
                !hideRoot ? /*#__PURE__*/ _jsx("li", {
                    children: isFaireDirectActiveInLoggedOut ? /*#__PURE__*/ _jsx(Anchor, {
                        href: faireBaseUrl(),
                        onClick: function() {
                            trackRetailerPortalBreadcrumbHomeView();
                        },
                        children: /*#__PURE__*/ _jsxs(Typography, {
                            children: [
                                /*#__PURE__*/ _jsx(LinkText, {
                                    children: homeMessage
                                }),
                                "  /  "
                            ]
                        })
                    }) : /*#__PURE__*/ _jsx(Button, {
                        style: {
                            textDecoration: "none"
                        },
                        variant: "tertiary",
                        linkTo: RetailerPaths.home(),
                        onClick: function() {
                            trackRetailerPortalBreadcrumbHomeView();
                        },
                        children: /*#__PURE__*/ _jsxs(Typography, {
                            children: [
                                /*#__PURE__*/ _jsx(LinkText, {
                                    children: homeMessage
                                }),
                                "  /  "
                            ]
                        })
                    })
                }) : null,
                options.map(function(option, index) {
                    return option.path ? /*#__PURE__*/ _jsx("li", {
                        children: /*#__PURE__*/ _jsx(Button, {
                            style: {
                                textDecoration: "none"
                            },
                            variant: "tertiary",
                            linkTo: option.path,
                            onClick: function(e) {
                                // We don't want to trim the list if we open in a new tab
                                if (e.ctrlKey || e.metaKey) {
                                    return;
                                }
                                handleClick(index, option);
                            },
                            children: /*#__PURE__*/ _jsxs(Typography, {
                                children: [
                                    /*#__PURE__*/ _jsx(LinkText, {
                                        children: option.label
                                    }),
                                    index !== options.length - 1 ? "  /  " : ""
                                ]
                            })
                        })
                    }, index) : /*#__PURE__*/ _jsx("li", {
                        children: /*#__PURE__*/ _jsxs(Typography, {
                            color: Core.text.subdued,
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    children: option.label
                                }),
                                index !== options.length - 1 ? "  /  " : ""
                            ]
                        })
                    }, index);
                })
            ]
        })
    });
};
var BreadcrumbRow = styled(Row).attrs({
    align: "flex-start"
}).withConfig({
    componentId: "sc-db7b35e8-0"
})([
    "&&&{display:block;}line-height:16px;max-width:1700px;margin:auto;width:100%;",
    "{padding:",
    " ",
    ";}",
    "{padding:",
    " ",
    ";}"
], responsiveMediaQueries.mobile, function(props) {
    return props.$verticalPadding || "12px";
}, function(props) {
    return props.$horizontalPadding || "16px";
}, responsiveMediaQueries.tabletAndAbove, function(props) {
    return props.$verticalPadding || "12px";
}, function(props) {
    return props.$horizontalPadding || "24px";
});
var LinkText = styled.span.withConfig({
    componentId: "sc-db7b35e8-1"
})([
    "&:hover{border-bottom:1px solid ",
    ";}"
], Core.border.default);
var StyledList = styled.ol.withConfig({
    componentId: "sc-db7b35e8-2"
})([
    "list-style-type:none;li{display:inline-block;padding-right:4px;}"
]);
